import { useParams } from "react-router-dom";

import { capsOf, type McpCatalog, type McpQuotaTier } from "../../api/mcp";
import { RecentChanges } from "../../components/audit/RecentChanges";
import { Breadcrumbs } from "../../components/Layout";
import {
  CouldNotLook,
  Denied,
  EmptyByDesign,
  LoadFailed,
  Loading,
  SignedOut,
} from "../../components/states";
import { CapEditor } from "../../components/mcp/CapEditor";
import { TierRateLimits } from "../../components/mcp/TierRateLimits";
import { RemoveOverride } from "../../components/mcp/RemoveOverride";
import { Page } from "../../components/ui";
import type { AppConfig } from "../../config";
import { useResource } from "../../hooks/useResource";

/**
 * One tier's caps, with every tool listed — not just the ones with rows.
 *
 * A page showing ten rows out of ninety reads as "the rest are unmanaged" when
 * the truth is "the rest inherit the * default". Listing all of them, with
 * where each value comes from, is the difference.
 */
export function CapsTier({ config }: { config: AppConfig }) {
  const { tier = "" } = useParams();
  const quota = useResource<McpQuotaTier>(
    config,
    `/mcp/admin/quota/${encodeURIComponent(tier)}`,
    [tier],
  );
  const catalog = useResource<McpCatalog>(config, "/mcp/admin/catalog");
  const s = quota.state;

  return (
    <Page title={<span className="ref">{tier}</span>} subtitle="Daily credit caps for this tier.">
      <Breadcrumbs
        trail={[
          { label: "Overview", to: "/" },
          { label: "Tiers & limits", to: "/policy/tiers" },
          { label: tier },
        ]}
      />

      {s.kind === "loading" && <Loading what="caps" />}
      {s.kind === "signedout" && <SignedOut />}
      {s.kind === "denied" && <Denied message={s.error.message} />}
      {s.kind === "unavailable" && (
        <CouldNotLook what="this tier's caps" message={s.error.message} onRetry={quota.reload} />
      )}
      {s.kind === "notfound" && <LoadFailed what="this tier" message="Route not found." />}
      {s.kind === "error" && <LoadFailed what="this tier" message={s.error.message} />}

      {s.kind === "ready" &&
        (() => {
          const caps = capsOf(s.data);
          const cat = catalog.state.kind === "ready" ? catalog.state.data : null;

          if (Object.keys(caps).length === 0) {
            return <EmptyByDesign scope={`tier ${tier}`} />;
          }

          return (
            <>
              {/* The unprotected-resolver warning that used to live here is
                  GONE — MCP removed both `unprotected_resolvers` and
                  `resolver_report_dependents` along with the resolver-cap
                  guard (RESOLVER_GUARD_REMOVED in api/mcp.ts). The warning
                  that matters now lives in the cap editor itself, at the
                  moment a search_* tool is actually being capped. */}

              <div className="mt-4">
                <CapEditor
                  config={config}
                  tier={tier}
                  caps={caps}
                  catalog={cat}
                  onDone={quota.reload}
                />
              </div>

              {/* The other half of editing a cap: taking one away. Without it
                  an override added by mistake could only be overwritten with
                  another number, never removed. */}
              <div className="mt-6">
                <RemoveOverride config={config} tier={tier} caps={caps} onDone={quota.reload} />
              </div>

              {/* RATE LIMITS LIVE HERE NOW. They were two lane cards in
                  global Settings writing `PATCH /admin/ratelimit/{lane}`,
                  which MCP retired — they are part of the tier document and
                  are set with the caps above, on the same route. A lane set
                  globally was always a fiction: the runtime applies it per
                  tier. */}
              <div className="mt-6">
                <TierRateLimits
                  config={config}
                  tier={tier}
                  quota={s.data}
                  onDone={quota.reload}
                />
              </div>
            </>
          );
        })()}
      <div className="mt-4">
        <RecentChanges config={config} domain="quota" target={tier} />
      </div>

    </Page>
  );
}
